/**
 * socket manager: handles websocket connection, reconnection, and message dispatching
 */

export interface SocketMessage {
  service: string;
  action: string;
  payload: Record<string, any>;
}

export type SocketListener = (message: SocketMessage) => void;
export type ReconnectCallback = () => void;

const INITIAL_RETRY_DELAY = 1000; // 1s
const MAX_RETRY_DELAY = 30000; // 30s
const RETRY_BACKOFF = 1.5;

/**
 * gather client fingerprint data for session identification
 */
export function getClientFingerprint(): Record<string, any> {
  return {
    userAgent: navigator.userAgent,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    screenColorDepth: window.screen.colorDepth,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    localtimeFormat: new Date().toString(),
    language: navigator.language,
    languages: navigator.languages,
    platform: navigator.platform,
    hardwareConcurrency: navigator.hardwareConcurrency,
    deviceMemory: (navigator as any).deviceMemory,
    maxTouchPoints: navigator.maxTouchPoints,
    doNotTrack: navigator.doNotTrack,
    mediaCapabilities: navigator.mediaCapabilities,
    permissions: !!navigator.permissions,
    mediaDevices: !!navigator.mediaDevices,
    clipboard: !!navigator.clipboard,
    geolocation: !!navigator.geolocation,
    serviceWorker: !!navigator.serviceWorker,
    webgl: !!document.createElement("canvas").getContext("webgl"),
    localStorage: (() => {
      try {
        return !!window.localStorage;
      } catch {
        return false;
      }
    })(),
    indexedDb: !!window.indexedDB,
    sessionStorage: !!window.sessionStorage,
  };
}

export class SocketManager {
  private ws: WebSocket | null = null;
  private url: string;
  private listeners = new Map<string, SocketListener[]>();
  private retryCount = 0;
  private retryTimeout: ReturnType<typeof setTimeout> | null = null;
  private shouldReconnect = true;
  private onReconnect: ReconnectCallback | null = null;

  constructor(url: string) {
    this.url = url;
  }

  /**
   * connect to websocket with automatic reconnection on failure
   */
  async connect(): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return;
    }

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(this.url);

        this.ws.onopen = () => {
          console.log("websocket connected");
          this.retryCount = 0;
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data) as SocketMessage;
            this.dispatchMessage(message);
          } catch (err) {
            console.error("failed to parse message:", err);
          }
        };

        this.ws.onerror = (event) => {
          console.error("websocket error:", event);
          reject(new Error("websocket connection error"));
        };

        this.ws.onclose = () => {
          console.log("websocket closed");
          this.ws = null;
          if (this.shouldReconnect) {
            this.scheduleReconnect();
          }
        };
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }

    const delay = Math.min(
      INITIAL_RETRY_DELAY * Math.pow(RETRY_BACKOFF, this.retryCount),
      MAX_RETRY_DELAY,
    );

    console.log(`scheduling reconnect in ${delay}ms (attempt ${this.retryCount + 1})`);
    this.retryCount++;

    this.retryTimeout = setTimeout(() => {
      if (this.shouldReconnect) {
        console.log(`[SocketManager] attempting to reconnect (attempt ${this.retryCount})`);
        this.connect()
          .then(() => {
            console.log(`[SocketManager] ✅ reconnected successfully`);
            // trigger reconnect callback to re-authenticate
            if (this.onReconnect) {
              console.log(`[SocketManager] calling onReconnect callback`);
              this.onReconnect();
            }
          })
          .catch((err) => {
            console.error("reconnection failed:", err);
          });
      }
    }, delay);
  }

  /**
   * set callback to execute when socket reconnects
   */
  setOnReconnect(callback: ReconnectCallback | null): void {
    this.onReconnect = callback;
  } /**
   * disconnect and stop reconnection attempts
   */
  disconnect(): void {
    this.shouldReconnect = false;

    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * send message to server
   */
  send(message: SocketMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn("websocket not connected");
      return;
    }

    try {
      this.ws.send(JSON.stringify(message));
    } catch (err) {
      console.error("failed to send message:", err);
    }
  }

  /**
   * register listener for specific action
   * returns unsubscribe function
   */
  on(action: string, callback: SocketListener): () => void {
    if (!this.listeners.has(action)) {
      this.listeners.set(action, []);
    }

    this.listeners.get(action)!.push(callback);

    return () => {
      const callbacks = this.listeners.get(action);
      if (callbacks) {
        const idx = callbacks.indexOf(callback);
        if (idx > -1) {
          callbacks.splice(idx, 1);
        }
      }
    };
  }

  /**
   * dispatch message to all listeners for that action
   */
  private dispatchMessage(message: SocketMessage): void {
    const callbacks = this.listeners.get(message.action);
    if (callbacks) {
      callbacks.forEach((cb) => {
        try {
          cb(message);
        } catch (err) {
          console.error(`error in listener for "${message.action}":`, err);
        }
      });
    }
  }

  /**
   * check if websocket is ready to send messages
   */
  isReady(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * get current connection status
   */
  getStatus(): "idle" | "connecting" | "connected" | "disconnected" | "error" {
    if (!this.ws) return "idle";
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return "connecting";
      case WebSocket.OPEN:
        return "connected";
      case WebSocket.CLOSED:
        return "disconnected";
      default:
        return "error";
    }
  }
}
